// SHADOW MARKET™ — Modal de pago on-chain con R₿L (25% dto · Base L2).
// Providers Wagmi/QueryClient autocontenidos (no toca providers globales).
// Estados: conectar → red → balance → dirección+confirmar → procesando →
//          verificación backend → éxito | error.
import React, { useState, useEffect, useCallback } from "react";
import {
  WagmiProvider, useAccount, useConnect, useDisconnect, useSwitchChain,
  useReadContract, useWriteContract, useWaitForTransactionReceipt,
} from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { formatUnits } from "viem";
import { Link } from "react-router-dom";
import {
  X, Wallet, Loader2, CheckCircle2, AlertTriangle, ExternalLink,
  Coins, Flame, RefreshCw, ShoppingBag,
} from "lucide-react";
import { wagmiConfig, BASE_CHAIN_ID } from "../../web3/wagmiConfig";
import { RBL_CONTRACT, TREASURY_WALLET, rblTransferAbi } from "../../web3/rblPay";
import { API, safeJson } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { useMarketRblI18n } from "../../i18n/market-rbl";

const COUNTRIES = [
  "ES", "MX", "CO", "AR", "CL", "PE", "UY", "DE", "FR",
  "IT", "PT", "NL", "BE", "GB", "IE", "AT", "DK", "SE", "FI",
];

const CONNECTOR_LABELS = (t) => ({
  injected: t.injected,
  coinbaseWalletSDK: "Coinbase Wallet",
  walletConnect: "WalletConnect",
});

const fmtRbl = (wei) =>
  Number(formatUnits(wei, 18)).toLocaleString(undefined, { maximumFractionDigits: 0 });

const inputCls =
  "w-full rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-xs text-white placeholder-white/30 outline-none transition-colors focus:border-orange-400/60";

function ModalInner({ onClose }) {
  const { lang } = useLanguage();
  const t = useMarketRblI18n(lang);
  const { user, isPro } = useAuth();
  const { items, clear } = useCart();

  const { address, isConnected, chainId: walletChainId } = useAccount();
  const { connectors, connect, isPending: isConnecting, error: connectError } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitching } = useSwitchChain();
  const wrongNetwork = isConnected && walletChainId !== BASE_CHAIN_ID;

  // Cotización del carrito (backend re-precia + convierte a R₿L). 60s refresh.
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(false);
  const loadQuote = useCallback(async () => {
    try {
      const r = await fetch(`${API}/shadow-market/rbl-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({
            productId: i.id, type: i.type, name: i.name,
            color: i.color, size: i.size, quantity: i.quantity,
            customization: i.customization
              ? {
                  base_color: i.customization.baseColor || null,
                  text: i.customization.text || null,
                  text_color: i.customization.textColor || null,
                  image_data_url: i.customization.imageDataUrl || null,
                  preview_data_url: i.customization.previewDataUrl || null,
                }
              : null,
          })),
          is_pro: !!isPro,
          user_id: user?.id || null,
        }),
      });
      if (!r.ok) throw new Error("quote_failed");
      const q = await safeJson(r);
      setQuote(q);
      setQuoteError(false);
    } catch {
      setQuoteError(true);
    }
  }, [items, isPro, user]);

  useEffect(() => {
    loadQuote();
    const id = setInterval(loadQuote, 60_000);
    return () => clearInterval(id);
  }, [loadQuote]);

  const amountWei = quote ? BigInt(quote.amount_wei) : 0n;

  // Balance R₿L de la wallet
  const { data: balance } = useReadContract({
    address: RBL_CONTRACT, abi: rblTransferAbi, functionName: "balanceOf",
    args: [address], chainId: BASE_CHAIN_ID,
    query: { enabled: isConnected && !!address, refetchInterval: 20_000 },
  });
  const insufficient = isConnected && quote && balance != null && balance < amountWei;

  // Dirección de envío + email
  const [addr, setAddr] = useState({
    full_name: "", line1: "", line2: "", city: "", postal_code: "", country: "ES", phone: "",
  });
  const [email, setEmail] = useState(user?.email || "");
  const setA = (k) => (e) => setAddr((p) => ({ ...p, [k]: e.target.value }));
  const addrValid =
    addr.full_name.trim().length >= 2 && addr.line1.trim().length >= 3 &&
    addr.city.trim().length >= 1 && addr.postal_code.trim().length >= 2 && !!addr.country;

  // TX de transferencia
  const { data: txHash, isPending: isWalletConfirm, writeContract, error: txError, reset: resetTx } = useWriteContract();
  const { isLoading: isMining, isSuccess: isMined } = useWaitForTransactionReceipt({ hash: txHash, chainId: BASE_CHAIN_ID });

  // Verificación backend tras confirmación on-chain
  const [verifying, setVerifying] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [verifyError, setVerifyError] = useState(null);

  const verifyOrder = useCallback(async (hash) => {
    setVerifying(true);
    setVerifyError(null);
    try {
      const r = await fetch(`${API}/shadow-market/pay-rbl`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({
            productId: i.id, type: i.type, name: i.name,
            color: i.color, size: i.size, quantity: i.quantity,
            customization: i.customization
              ? {
                  base_color: i.customization.baseColor || null,
                  text: i.customization.text || null,
                  text_color: i.customization.textColor || null,
                  image_data_url: i.customization.imageDataUrl || null,
                  preview_data_url: i.customization.previewDataUrl || null,
                }
              : null,
          })),
          is_pro: !!isPro,
          user_id: user?.id || null,
          user_email: email || user?.email || null,
          wallet_address: address,
          tx_hash: hash,
          shipping_address: {
            full_name: addr.full_name.trim(),
            line1: addr.line1.trim(),
            line2: addr.line2.trim() || null,
            city: addr.city.trim(),
            postal_code: addr.postal_code.trim(),
            country: addr.country,
            phone: addr.phone.trim() || null,
          },
        }),
      });
      const d = await safeJson(r);
      if (!r.ok) {
        const det = d?.detail || {};
        throw new Error(det?.error || det?.message || "verification_failed");
      }
      setOrderData(d);
      clear(); // vaciar carrito — pedido pagado
    } catch (e) {
      setVerifyError(e.message || "verification_failed");
    } finally {
      setVerifying(false);
    }
  }, [items, isPro, user, email, address, addr, clear]);

  useEffect(() => {
    if (isMined && txHash && !orderData && !verifying && !verifyError) {
      verifyOrder(txHash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMined, txHash]);

  const pay = () => {
    setVerifyError(null);
    writeContract({
      address: RBL_CONTRACT, abi: rblTransferAbi, functionName: "transfer",
      chainId: BASE_CHAIN_ID,
      args: [TREASURY_WALLET, amountWei],
    });
  };

  const busy = isWalletConfirm || isMining || verifying;
  const labels = CONNECTOR_LABELS(t);

  return (
    <div
      className="fixed inset-0 z-[9500] flex items-center justify-center bg-black/80 p-4"
      onClick={busy ? undefined : onClose}
      data-testid="market-rbl-modal"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-[1.5px]"
        style={{ background: "linear-gradient(135deg,#f97316,#fbbf24)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[88vh] overflow-y-auto rounded-2xl p-5" style={{ background: "#0a0a0f" }}>
          {/* Cabecera */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="flex items-center gap-1.5 text-sm font-black uppercase tracking-[0.14em] text-white">
                <ShoppingBag className="h-4 w-4 text-orange-400" /> {t.payWithRBL}
              </h3>
              <p className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
                On-chain · Base L2 · {t.discount25}
              </p>
            </div>
            {!busy && (
              <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="market-rbl-close">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Desglose de precios — siempre visible */}
          <div className="mt-4 rounded-xl p-4" style={{ background: "#111118" }}>
            {quoteError ? (
              <p className="flex items-start gap-2 text-[11px] text-amber-300" data-testid="market-rbl-price-unavailable">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t.priceUnavailable}
              </p>
            ) : !quote ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-orange-400" />
              </div>
            ) : (
              <>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between text-white/55">
                    <span>{t.originalPrice}</span>
                    <span className="line-through">€{quote.subtotal_eur.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between" style={{ color: "#fb923c" }}>
                    <span>{t.discountRBL}</span>
                    <span>-€{quote.discount_eur.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-white/55">
                    <span>{t.shipping}</span>
                    <span className={quote.shipping_eur === 0 ? "text-emerald-300" : ""}>
                      {quote.shipping_eur === 0 ? t.shippingFree : `€${quote.shipping_eur.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-dashed border-white/10 pt-1.5 font-black text-white">
                    <span>{t.finalPrice}</span>
                    <span data-testid="market-rbl-total-eur">€{quote.total_eur.toFixed(2)}</span>
                  </div>
                </div>
                <p className="mt-2 font-mono text-lg font-black" style={{ color: "#fb923c" }} data-testid="market-rbl-amount">
                  {Number(quote.rbl_required).toLocaleString()} R₿L
                </p>
                <div className="mt-1 flex items-center justify-between font-mono text-[10px] text-white/40">
                  <span>{t.currentPrice}: ${Number(quote.rbl_price_usd).toFixed(8)}</span>
                  <button
                    onClick={loadQuote}
                    className="inline-flex items-center gap-1 text-orange-300/80 hover:text-orange-200"
                    data-testid="market-rbl-refresh-price"
                  >
                    <RefreshCw className="h-2.5 w-2.5" /> {t.updatePrice}
                  </button>
                </div>
                <p className="mt-1 text-[9px] font-mono text-white/30">{t.priceNote}</p>
              </>
            )}
          </div>

          {/* Máquina de estados */}
          {orderData ? (
            <div
              className="mt-4 rounded-xl border p-4 text-center"
              style={{ borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)" }}
              data-testid="market-rbl-success"
            >
              <CheckCircle2 className="mx-auto h-8 w-8" style={{ color: "#22c55e" }} />
              <p className="mt-2 text-sm font-black text-white">{t.orderConfirmed}</p>
              <p className="mt-1 font-mono text-xs text-white/70" data-testid="market-rbl-order-number">
                {t.orderNumber}: <b>{orderData.order_number}</b>
              </p>
              {/* Desglose 95/5 — burning mecánico del Shadow Market */}
              <div className="mt-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2.5 text-left">
                <p className="font-mono text-[11px] text-white/70" data-testid="market-rbl-paid-amount">
                  {t.youPaid}: <b className="text-white">{Number(orderData.amount_rbl).toLocaleString(undefined, { maximumFractionDigits: 0 })} R₿L</b>
                </p>
                <p className="mt-1.5 text-[11px] text-white/60">{t.toAcademyNote}</p>
                <p className="mt-0.5 text-[11px] font-bold" style={{ color: "#fb923c" }} data-testid="market-rbl-burn-note">
                  {t.marketBurnNote}
                </p>
                {orderData.burn_tx_hash && (
                  <a
                    href={`https://basescan.org/tx/${orderData.burn_tx_hash}`} target="_blank" rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold underline" style={{ color: "#fb923c" }}
                    data-testid="market-rbl-burn-tx-link"
                  >
                    {t.viewBurnTx} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              <p className="mt-2 text-[11px] text-white/55">{t.successSub}</p>
              <a
                href={`https://basescan.org/tx/${orderData.tx_hash}`} target="_blank" rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold underline" style={{ color: "#22c55e" }}
              >
                {t.viewTx} <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : busy ? (
            <div className="mt-4 rounded-xl p-4 text-center" style={{ background: "#111118" }} data-testid="market-rbl-processing">
              <Loader2 className="mx-auto h-7 w-7 animate-spin" style={{ color: "#f97316" }} />
              <p className="mt-2 text-xs font-bold text-white">
                {isWalletConfirm ? t.walletConfirm : isMining ? t.processing : t.verifying}
              </p>
              {txHash && (
                <a
                  href={`https://basescan.org/tx/${txHash}`} target="_blank" rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-white/40 hover:text-white"
                >
                  {txHash.slice(0, 10)}…{txHash.slice(-8)} <ExternalLink className="h-2.5 w-2.5" />
                </a>
              )}
            </div>
          ) : !isConnected ? (
            <div className="mt-4" data-testid="market-rbl-connect">
              <p className="mb-2 text-[11px] font-mono uppercase tracking-[0.18em] text-white/40">{t.selectWallet}</p>
              <div className="space-y-2">
                {connectors.map((c) => (
                  <button
                    key={c.uid} disabled={isConnecting} onClick={() => connect({ connector: c })}
                    data-testid={`market-rbl-connector-${c.id}`}
                    className="flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    style={{ borderColor: "rgba(249,115,22,0.4)", background: "#111118" }}
                  >
                    <span>{labels[c.id] || c.name}</span>
                    <Wallet className="h-4 w-4" style={{ color: "#f97316" }} />
                  </button>
                ))}
              </div>
              {connectError && (
                <p className="mt-2 break-words text-[11px]" style={{ color: "#f87171" }}>
                  {connectError.shortMessage || connectError.message}
                </p>
              )}
            </div>
          ) : wrongNetwork ? (
            <div
              className="mt-4 flex items-center justify-between gap-2 rounded-xl border px-3 py-3"
              style={{ borderColor: "rgba(249,115,22,0.5)", background: "rgba(249,115,22,0.08)" }}
              data-testid="market-rbl-wrong-net"
            >
              <span className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#f97316" }}>
                <AlertTriangle className="h-3.5 w-3.5" /> {t.wrongNet}
              </span>
              <button
                onClick={() => switchChain({ chainId: BASE_CHAIN_ID })} disabled={isSwitching}
                className="shrink-0 rounded-full px-3 py-1.5 text-[11px] font-black uppercase disabled:opacity-50"
                style={{ background: "#f97316", color: "#0a0a0f" }} data-testid="market-rbl-switch"
              >
                {t.switchBase}
              </button>
            </div>
          ) : insufficient ? (
            <div
              className="mt-4 rounded-xl border p-4"
              style={{ borderColor: "rgba(248,113,113,0.4)", background: "rgba(248,113,113,0.06)" }}
              data-testid="market-rbl-insufficient"
            >
              <p className="text-xs font-bold" style={{ color: "#f87171" }}>
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> {t.insufficientBalance}
              </p>
              <p className="mt-1 text-[11px] text-white/55">
                {t.yourBalance}: <b className="text-white">{fmtRbl(balance ?? 0n)} R₿L</b> · {t.missing}{" "}
                <b style={{ color: "#f97316" }}>{fmtRbl(amountWei - (balance ?? 0n))} R₿L</b>
              </p>
              <Link
                to="/shadow/tokens" onClick={onClose} data-testid="market-rbl-buy-link"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-xs font-black uppercase tracking-[0.18em]"
                style={{ background: "#f97316", color: "#0a0a0f" }}
              >
                <Coins className="h-4 w-4" /> {t.buyRBL} →
              </Link>
            </div>
          ) : (
            <div className="mt-4" data-testid="market-rbl-confirm">
              <div className="flex items-center justify-between text-[11px] text-white/55">
                <span>
                  <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ background: "#22c55e" }} />
                  <span className="font-mono">{address?.slice(0, 6)}…{address?.slice(-4)}</span>
                </span>
                <span>
                  {t.yourBalance}:{" "}
                  <b className="text-white" data-testid="market-rbl-balance">
                    {balance != null ? fmtRbl(balance) : "…"} R₿L
                  </b>
                </span>
              </div>

              {/* Dirección de envío */}
              <p className="mt-3 text-[10px] font-black uppercase tracking-[0.2em] text-white/45">
                {t.shippingTitle}
              </p>
              <div className="mt-2 space-y-2">
                <input className={inputCls} placeholder={t.fullName} value={addr.full_name} onChange={setA("full_name")} data-testid="market-rbl-addr-name" />
                <input className={inputCls} placeholder={t.addressLine1} value={addr.line1} onChange={setA("line1")} data-testid="market-rbl-addr-line1" />
                <input className={inputCls} placeholder={t.addressLine2} value={addr.line2} onChange={setA("line2")} data-testid="market-rbl-addr-line2" />
                <div className="flex gap-2">
                  <input className={inputCls} placeholder={t.city} value={addr.city} onChange={setA("city")} data-testid="market-rbl-addr-city" />
                  <input className={inputCls} style={{ maxWidth: 110 }} placeholder={t.postalCode} value={addr.postal_code} onChange={setA("postal_code")} data-testid="market-rbl-addr-zip" />
                </div>
                <div className="flex gap-2">
                  <select className={inputCls} value={addr.country} onChange={setA("country")} data-testid="market-rbl-addr-country" style={{ appearance: "auto" }}>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c} style={{ background: "#111118" }}>{c}</option>
                    ))}
                  </select>
                  <input className={inputCls} placeholder={t.phone} value={addr.phone} onChange={setA("phone")} data-testid="market-rbl-addr-phone" />
                </div>
                <input className={inputCls} type="email" placeholder={t.emailLabel} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="market-rbl-email" />
              </div>
              {!addrValid && (
                <p className="mt-2 text-[10px] text-white/35">{t.fillShipping}</p>
              )}

              <button
                onClick={pay}
                disabled={!quote || balance == null || !addrValid}
                data-testid="market-rbl-pay-btn"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-black uppercase tracking-[0.16em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-40"
                style={{ background: "linear-gradient(135deg,#f97316,#fbbf24)" }}
              >
                <Flame className="h-4 w-4" /> {t.confirmPayment}
              </button>
              <button
                onClick={() => disconnect()}
                className="mt-2 w-full text-center text-[10px] font-mono uppercase tracking-wider text-white/35 hover:text-white/60"
                data-testid="market-rbl-disconnect"
              >
                {address?.slice(0, 6)}… · {t.disconnect}
              </button>
            </div>
          )}

          {/* Errores */}
          {(txError || verifyError) && !orderData && !busy && (
            <div
              className="mt-3 rounded-lg border px-3 py-2"
              style={{ borderColor: "rgba(248,113,113,0.4)", background: "rgba(248,113,113,0.06)" }}
              data-testid="market-rbl-error"
            >
              <p className="break-words text-[11px]" style={{ color: "#f87171" }}>
                <b>{t.error}:</b> {verifyError || txError?.shortMessage || txError?.message}
              </p>
              {verifyError && txHash ? (
                <button
                  onClick={() => verifyOrder(txHash)}
                  className="mt-1.5 text-[11px] font-black uppercase underline"
                  style={{ color: "#f97316" }}
                  data-testid="market-rbl-retry-verify"
                >
                  {t.retryVerify}
                </button>
              ) : txError ? (
                <button
                  onClick={() => resetTx()}
                  className="mt-1.5 text-[11px] font-black uppercase underline"
                  style={{ color: "#f97316" }}
                >
                  {t.retry}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const queryClient = new QueryClient();

export default function RBLMarketCheckoutModal({ onClose }) {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <ModalInner onClose={onClose} />
      </QueryClientProvider>
    </WagmiProvider>
  );
}
